import { WebRTCAdaptor } from "@antmedia/webrtc_adaptor";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const DEFAULT_RECONNECT = {
  enabled: true,
  maxRetries: 8,
  baseDelayMs: 1000,
  maxDelayMs: 15000,
};


const getServerUrl = () => AuthenticationService?.getTurnServerUrl();
const getUsername = () => AuthenticationService?.getTurnUserName();
const getCredential = () => AuthenticationService?.getTurnPassword();

const buildIceServers = () => {
  const url = getServerUrl();
  const username = getUsername();
  const credential = getCredential();
  const servers = [{ urls: "stun:stun.l.google.com:19302" }];
  if (url && username && credential) {
    servers.push({
      urls: [
        url,
        url.replace("?transport=udp", "?transport=tcp"),
        url.replace("turn:", "turns:").replace("?transport=udp", "?transport=tcp"),
      ],
      username,
      credential,
    });
  }
  return servers;
};

export default class V3MediaPTT {
  constructor({
    websocketUrl,
    streamId,
    roomId,
    onRemoteTrack = () => { },
    peerconnection_config = { iceServers: buildIceServers() },
    onInitialized = () => { },
    onStateChange = () => { },
    onError = (e) => {
      console.error(e);
    },
    dataChannelEnabled = true,
    myUserNumber = AuthenticationService.getExtensionNumber(),
    reconnect = DEFAULT_RECONNECT,
  }) {
    if (!websocketUrl || !streamId || !roomId) {
      throw new Error("websocketUrl, streamId and roomId are required");
    }

    this.websocketUrl = websocketUrl;
    this.streamId = streamId;
    this.roomId = roomId;
    this.onRemoteTrack = onRemoteTrack;
    this.peerconnection_config = peerconnection_config;
    this.onInitialized = onInitialized;
    this.onStateChange = onStateChange;
    this.onError = onError;
    this.dataChannelEnabled = dataChannelEnabled;
    this.myUserNumber = myUserNumber ? String(myUserNumber) : null;

    this.reconnect = { ...DEFAULT_RECONNECT, ...reconnect };
    this.retryCount = 0;
    this.reconnectTimer = null;
    this.lastStartConfig = null;

    this.instanceId = `${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    this.participants = new Set();
    this.singleUserTimer = null;
    this.autoDisconnectTimeout = 10000; // 10 seconds

    this.webRTCAdaptor = null;
    this.videoAdaptor = null;
    this.localPreviewStream = null;
    this.playAdaptors = {};

    this.audioElement = new Audio();
    this.audioElement.autoplay = true;
    this.audioElement.playsInline = true;
    this.audioElement.muted = false;
    this.audioUnlocked = false;

    this.remoteTracks = {};
    this.closed = false;
    this.state = "idle";
  }

  _setState(next, meta = {}) {
    this.state = next;
    try {
      this.onStateChange(next, { retryCount: this.retryCount, ...meta });
    } catch { }
  }

  _clearSingleUserTimer() {
    if (this.singleUserTimer) {
      clearTimeout(this.singleUserTimer);
      this.singleUserTimer = null;
      console.log("⏰ [AutoDisconnect] Timer cleared");
    }
  }

  _startSingleUserTimer() {
    this._clearSingleUserTimer();
    console.log("⏰ [AutoDisconnect] Starting 10s timer for single user");
    this.singleUserTimer = setTimeout(() => {
      if (this.participants.size === 1) {
        console.log("⏰ [AutoDisconnect] Timer expired, participants still 1, triggering auto-disconnect");
        this._setState("autoDisconnect");
      } else {
        console.log("⏰ [AutoDisconnect] Timer expired but participants >1, no action");
      }
    }, this.autoDisconnectTimeout);
  }

  _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _scheduleReconnect(reason) {
    console.log("📡 [V3MediaPTT] Scheduling reconnect", {
      streamId: this.streamId,
      roomId: this.roomId,
      retryCount: this.retryCount,
      reason,
      closed: this.closed,
      reconnectEnabled: this.reconnect.enabled,
      lastStartConfig: Boolean(this.lastStartConfig),
    });

    if (this.closed || !this.reconnect.enabled) {
      console.log("📡 [V3MediaPTT] Reconnect skipped", {
        closed: this.closed,
        reconnectEnabled: this.reconnect.enabled,
      });
      return;
    }
    if (!this.lastStartConfig) {
      console.log("📡 [V3MediaPTT] Reconnect skipped because no lastStartConfig");
      return;
    }
    if (this.retryCount >= this.reconnect.maxRetries) {
      this.onError(`Reconnect failed after ${this.retryCount} attempts (${reason})`);
      return;
    }
    this._clearReconnect();
    const nextRetry = this.retryCount + 1;
    const base = this.reconnect.baseDelayMs * Math.pow(2, this.retryCount);
    const delay = Math.min(this.reconnect.maxDelayMs, base);
    const jitter = Math.floor(delay * 0.2 * Math.random());
    this.retryCount = nextRetry;
    this._setState("reconnecting", { retryCount: nextRetry });
    this.reconnectTimer = setTimeout(() => {
      this._reconnect(reason);
    }, delay + jitter);
  }

  _reconnect(reason) {
    console.log("📡 [V3MediaPTT] Reconnect attempt", {
      streamId: this.streamId,
      roomId: this.roomId,
      retryCount: this.retryCount,
      reason,
    });
    if (this.closed || !this.lastStartConfig) {
      console.log("📡 [V3MediaPTT] Reconnect aborted", {
        closed: this.closed,
        lastStartConfig: Boolean(this.lastStartConfig),
      });
      return;
    }
    this._startPublish({ ...this.lastStartConfig, isReconnect: true, reason });
  }

  _cleanupAdaptor(adaptor, streamId, roomId) {
    if (!adaptor) return;
    console.log("📡 [V3MediaPTT] Cleaning up adaptor", {
      streamId,
      roomId,
      hasWebSocketAdaptor: Boolean(adaptor.webSocketAdaptor),
      hasRemotePeerConnection: Boolean(adaptor.remotePeerConnection),
      hasMediaManager: Boolean(adaptor.mediaManager),
    });

    try {
      adaptor.stop?.(streamId);
    } catch (err) {
      console.warn("⚠️ [V3MediaPTT] Adaptor stop error", err);
    }

    try {
      adaptor.leaveFromRoom?.(roomId);
    } catch { }

    try {
      const pcs = adaptor.remotePeerConnection;
      if (pcs && typeof pcs === "object") {
        Object.keys(pcs).forEach((k) => {
          try {
            pcs[k]?.close?.();
          } catch { }
        });
      }
    } catch { }

    try {
      adaptor.webSocketAdaptor?.close();
    } catch { }

    try {
      adaptor.mediaManager?.localStream?.getTracks().forEach((t) => t.stop());
    } catch { }

    try {
      adaptor.mediaManager?.audioContext?.close();
    } catch { }
  }

  _handleRemoteTrack(obj) {
    if (!obj) return;
    const track = obj.track;
    if (track && track.kind === "audio") {
      this.remoteTracks[track.id] = track;
      this._ensureAudioPlayback(track);
    }

    try {
      this.onRemoteTrack({
        instanceId: this.instanceId,
        track: obj.track,
        stream: obj.stream,
        streamId: obj.streamId,
        trackId: obj.trackId,
      });
    } catch { }
  }

  _ensureAudioPlayback(track) {
    if (!track) return;
    const stream = new MediaStream([track]);
    this.audioElement.srcObject = stream;

    if (!this.audioUnlocked) {
      this.audioElement
        .play()
        .then(() => {
          this.audioUnlocked = true;
        })
        .catch(() => { });
    } else {
      this.audioElement.play().catch(() => { });
    }
  }

  init() {
    return this._startPublish({
      localStream: null,
      enableVideo: false,
      roomId: this.roomId,
      autoPlayRoom: true,
      reason: "init",
    });
  }

  playPublish(localStream = null, roomId = null, isVideo = false) {
    if (roomId) this.roomId = roomId;
    return this._startPublish({
      localStream,
      enableVideo: !!isVideo,
      roomId: this.roomId,
      autoPlayRoom: true,
      reason: "playPublish",
    });
  }

  _startPublish({
    localStream,
    enableVideo,
    roomId,
    autoPlayRoom,
    isReconnect = false,
    reason,
  }) {
    return new Promise((resolve, reject) => {
      try {
        this._clearReconnect();
        if (!isReconnect) {
          this.retryCount = 0;
        }

        this.lastStartConfig = {
          localStream,
          enableVideo,
          roomId,
          autoPlayRoom,
        };

        if (this.webRTCAdaptor) {
          this._cleanupAdaptor(this.webRTCAdaptor, this.streamId, this.roomId);
          this.webRTCAdaptor = null;
        }

        this._setState("connecting");

        this.webRTCAdaptor = new WebRTCAdaptor({
          websocket_url: this.websocketUrl,
          mediaConstraints: {
            audio: true,
            video: enableVideo,
          },
          peerconnection_config: this.peerconnection_config,
          sdp_constraints: {
            OfferToReceiveAudio: true,
            OfferToReceiveVideo: true,
          },
          localStream,
          dataChannelEnabled: this.dataChannelEnabled,
          callback: (info, obj) => {
            if (this.closed) return;
            console.log("WebRTCAdaptor Callback:", info, obj);
            if (info === "initialized") {
              this._setState("connected");
              try {
                this.onInitialized(this.instanceId);
              } catch { }
              try {
                this.webRTCAdaptor.publish(
                  this.streamId,
                  null,
                  null,
                  null,
                  null,
                  roomId,
                  null,
                  null,
                );
              } catch (e) {
                this.onError(e);
              }
              resolve();
              return;
            }

            if (info === "publish_started") {
              this._setState("publishing");

              console.log("🚀 Publish Started");

              setTimeout(async () => {

                console.log(
                  "🎯 REMOTE PCS:",
                  this.webRTCAdaptor?.remotePeerConnection
                );

                const pcs =
                  this.webRTCAdaptor?.remotePeerConnection || {};

                const pcKeys = Object.keys(pcs);

                console.log("🎯 PC KEYS:", pcKeys);

                if (!pcKeys.length) {
                  console.log("❌ NO PEER CONNECTION FOUND");
                  return;
                }

                // First available peer connection
                const pc = pcs[pcKeys[0]];

                pc.onicecandidate = (event) => {
                  if (event.candidate) {
                    console.log(
                      "🧊 ICE CANDIDATE:",
                      event.candidate.candidate
                    );
                  }
                };

                pc.oniceconnectionstatechange = () => {
                  console.log(
                    "🧊 ICE STATE:",
                    pc.iceConnectionState
                  );
                };

                pc.onconnectionstatechange = () => {
                  console.log(
                    "🔗 CONNECTION STATE:",
                    pc.connectionState
                  );
                };

                console.log("🎯 USING PC:", pc);

                if (!pc) {
                  console.log("❌ PC NOT FOUND");
                  return;
                }

                try {
                  const stats = await pc.getStats();

                  stats.forEach((report) => {

                    // LOCAL CANDIDATES
                    if (report.type === "local-candidate") {

                      console.log("📍 LOCAL CANDIDATE");
                      console.log("candidateType:", report.candidateType);
                      console.log("protocol:", report.protocol);
                      console.log("ip:", report.ip);
                      console.log("port:", report.port);

                      if (report.candidateType === "relay") {
                        console.log("🔥 TURN SERVER WORKING");
                      }

                      if (report.candidateType === "srflx") {
                        console.log("🌍 STUN SERVER WORKING");
                      }

                      if (report.candidateType === "host") {
                        console.log("🏠 HOST NETWORK");
                      }
                    }

                    // ACTIVE CONNECTION
                    if (
                      report.type === "candidate-pair" &&
                      report.state === "succeeded"
                    ) {
                      console.log(
                        "✅ ACTIVE CANDIDATE PAIR WORKING"
                      );
                    }
                  });

                } catch (e) {
                  console.log("❌ TURN STATS ERROR:", e);
                }

              }, 5000);

              if (autoPlayRoom) {
                try {
                  this.webRTCAdaptor.play(roomId);
                } catch { }
              }

              return;
            }

            if (info === "newTrackAvailable" || info === "newStreamAvailable") {
              this._handleRemoteTrack(obj);
              return;
            }

            if (info === "data_received") {
              this.handleDataMessage(obj.streamId, obj.data);
              return;
            }

            if (
              info === "streamLeaved" ||
              info === "streamStopped" ||
              info === "play_finished"
            ) {
              try {
                this.onRemoteTrack({
                  instanceId: this.instanceId,
                  removed: true,
                  streamId: obj.streamId,
                });
              } catch { }
              return;
            }

            if (
              info === "closed" ||
              info === "disconnected" ||
              info === "websocket_closed"
            ) {
              this._scheduleReconnect(info);
            }

            if (info === "ice_connection_state_changed") {
              const state = obj?.state;
              if (state === "failed" || state === "disconnected") {
                this._scheduleReconnect(state);
              }
            }

            if (info === "subtrackAdded") {
              if (obj?.trackId) {
                this.participants.add(obj.trackId);
                console.log(`👥 [Participants] Added ${obj.trackId}, count: ${this.participants.size}`);
                if (this.participants.size === 1) {
                  this._startSingleUserTimer();
                } else if (this.participants.size >= 2) {
                  this._clearSingleUserTimer();
                }
              }
              return;
            }

            // if (info === "subtrackRemoved") {
            //   if (obj?.trackId) {
            //     this.participants.delete(obj.trackId);
            //     console.log(`👥 [Participants] Removed ${obj.trackId}, count: ${this.participants.size}`);
            //     if (this.participants.size < 2) {
            //       this._startSingleUserTimer();
            //     }
            //   }
            //   return;
            // }

            if (info === "newTrackAvailable") {
              this._clearSingleUserTimer();
              console.log("📡 [AutoDisconnect] New track available, clearing timer");
              return;
            }
            if (
              info === "subtrackRemoved" ||
              (info === "notification" && obj?.definition === "subtrackRemoved")
            ) {
              if (obj?.mainTrack === this.roomId) {
                this._setState("subtrackRemoved", obj);
              }
              return;
            }
          },
          callbackError: (err, msg) => {
            const text = msg || err;
            this.onError(text);
            if (!isReconnect) {
              reject(text);
            }
            this._scheduleReconnect(text || reason || "error");
          },
        });
      } catch (e) {
        this.onError(e);
        reject(e);
      }
    });
  }

  _createPlayAdaptor(targetStreamId) {
    if (this.closed) return;
    if (!targetStreamId) return;
    console.log(`📡 [PlayAdaptor] Creating play adaptor for streamId: ${targetStreamId}`);
    if (this.playAdaptors[targetStreamId]) {
      this._stopPlayStream(targetStreamId);
    }

    const playAdaptor = new WebRTCAdaptor({
      websocket_url: this.websocketUrl,
      mediaConstraints: { video: false, audio: false },
      peerconnection_config: this.peerconnection_config,
      sdp_constraints: {
        OfferToReceiveAudio: true,
        OfferToReceiveVideo: true,
      },
      dataChannelEnabled: false,
      callback: (info, obj) => {
        if (this.closed) return;
        console.log("📡 [PlayAdaptor] Callback:", info, obj);
        if (info === "initialized") {
          try {
            playAdaptor.play(targetStreamId);
          } catch (e) {
            this.onError(e);
          }
          return;
        }

        if (info === "newTrackAvailable" || info === "newStreamAvailable") {
          this._handleRemoteTrack(obj);
          return;
        }

        if (info === "play_finished") {
          this._stopPlayStream(targetStreamId);
        }
      },
      callbackError: (err, msg) => {
        console.log("📡 [PlayAdaptor] Error Callback:", err, msg);
        this.onError(msg || err);
        this._stopPlayStream(targetStreamId);
      },
    });

    this.playAdaptors[targetStreamId] = playAdaptor;
  }

  _stopPlayStream(targetStreamId) {
    const adaptor = this.playAdaptors[targetStreamId];
    if (!adaptor) return;
    console.log(`📡 [PlayAdaptor] Stopping play adaptor for streamId: ${targetStreamId}`);
    try {
      adaptor.stop(targetStreamId);
    } catch { }

    try {
      adaptor.webSocketAdaptor?.close();
    } catch { }

    try {
      Object.values(adaptor.remotePeerConnection || {}).forEach((pc) => {
        try {
          pc.close();
        } catch { }
      });
    } catch { }

    delete this.playAdaptors[targetStreamId];

    try {
      this.onRemoteTrack({
        instanceId: this.instanceId,
        removed: true,
        streamId: targetStreamId,
      });
    } catch { }
  }

  play(
    streamId,
    token,
    roomId,
    enableTracks,
    subscriberId,
    subscriberCode,
    metaData,
    role,
  ) {
    try {
      this.webRTCAdaptor?.play(
        streamId,
        token,
        roomId,
        enableTracks,
        subscriberId,
        subscriberCode,
        metaData,
        role,
      );
    } catch (e) {
      this.onError(e);
    }
  }

  handleDataMessage(streamId, message) {
    if (this.closed) return;

    try {
      if (streamId === this.streamId) return;

      let parsed = null;
      if (typeof message === "string") {
        try {
          parsed = JSON.parse(message);
        } catch { }
      } else if (typeof message === "object") {
        parsed = message;
      }


      if (parsed?.eventType?.includes("AUDIO_TRACK_ASSIGN")) {
        const payload = parsed.payload || [];
        const active = payload.find((p) => p.trackId && p.trackId !== "");
        if (active?.trackId) {
          const track = this.remoteTracks[active.trackId];
          if (track) {
            this._ensureAudioPlayback(track);
          }
        }
      }
    } catch (e) {
      this.onError(e);
    }
  }

  async press() {
    try {
      this.webRTCAdaptor?.unmuteLocalMic?.(this.streamId);
      return true;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  release() {
    try {
      this.webRTCAdaptor?.muteLocalMic?.(this.streamId);
      return true;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  setMicEnabled(enabled) {
    try {
      if (
        this.webRTCAdaptor &&
        typeof this.webRTCAdaptor.unmuteLocalMic === "function" &&
        typeof this.webRTCAdaptor.muteLocalMic === "function"
      ) {
        if (enabled) {
          this.webRTCAdaptor.unmuteLocalMic(this.streamId);
        } else {
          this.webRTCAdaptor.muteLocalMic(this.streamId);
        }
        return true;
      }

      if (this.webRTCAdaptor?.localStream) {
        this.webRTCAdaptor.localStream
          .getAudioTracks()
          .forEach((t) => (t.enabled = !!enabled));
        return true;
      }

      if (this.localPreviewStream) {
        this.localPreviewStream
          .getAudioTracks()
          .forEach((t) => (t.enabled = !!enabled));
        return true;
      }

      return false;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  setVideoEnabled(enabled) {
    try {
      const tracks =
        this.webRTCAdaptor?.localStream?.getVideoTracks?.() ||
        this.localPreviewStream?.getVideoTracks?.() ||
        [];
      if (tracks.length) {
        tracks.forEach((t) => (t.enabled = !!enabled));
        return true;
      }

      if (enabled && this.webRTCAdaptor?.turnOnLocalCamera) {
        this.webRTCAdaptor.turnOnLocalCamera(this.streamId);
        return true;
      }

      if (!enabled && this.webRTCAdaptor?.turnOffLocalCamera) {
        this.webRTCAdaptor.turnOffLocalCamera(this.streamId);
        return true;
      }

      return false;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  async upgradeToVideo(videoStream, streamId = this.streamId) {
    try {
      let stream = videoStream;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      if (this.webRTCAdaptor?.updateVideoTrack) {
        await this.webRTCAdaptor.updateVideoTrack(stream, streamId, null, true);
        return true;
      }

      if (this.webRTCAdaptor?.mediaManager?.updateVideoTrack) {
        await this.webRTCAdaptor.mediaManager.updateVideoTrack(
          stream,
          streamId,
          null,
          true,
        );
        return true;
      }

      return false;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  async startPreview(localVideoElement = null) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      this.localPreviewStream = stream;

      if (localVideoElement) {
        localVideoElement.srcObject = stream;
        localVideoElement.muted = true;
        await localVideoElement.play().catch(() => { });
      }

      this.videoAdaptor = new WebRTCAdaptor({
        websocket_url: this.websocketUrl,
        mediaConstraints: { video: true, audio: false },
        peerconnection_config: this.peerconnection_config,
        sdp_constraints: {
          OfferToReceiveAudio: false,
          OfferToReceiveVideo: true,
        },
        localStream: stream,
        dataChannelEnabled: false,
        callback: (info) => {
          if (this.closed) return;
          if (info === "initialized") {
            try {
              this.videoAdaptor.joinRoom(this.roomId, this.streamId + "_video");
            } catch { }
          }
        },
        callbackError: (err) => {
          this.onError(err);
        },
      });

      return true;
    } catch (e) {
      this.onError(e);
      throw e;
    }
  }

  async startPreviewWithStream(
    sourceStream,
    publishStreamId = null,
    localVideoElement = null,
  ) {
    try {
      if (!sourceStream) {
        throw new Error("sourceStream is required");
      }

      const videoTracks =
        (sourceStream.getVideoTracks && sourceStream.getVideoTracks()) || [];
      if (!videoTracks.length) {
        throw new Error("sourceStream has no video tracks");
      }

      const clonedTracks = videoTracks.map((t) => t.clone());
      const stream = new MediaStream(clonedTracks);
      this.localPreviewStream = stream;

      if (localVideoElement) {
        localVideoElement.srcObject = stream;
        localVideoElement.muted = true;
        await localVideoElement.play().catch(() => { });
      }

      const publishId = publishStreamId || this.streamId + "_video";

      this.videoAdaptor = new WebRTCAdaptor({
        websocket_url: this.websocketUrl,
        mediaConstraints: { video: true, audio: false },
        peerconnection_config: this.peerconnection_config,
        sdp_constraints: {
          OfferToReceiveAudio: false,
          OfferToReceiveVideo: true,
        },
        localStream: stream,
        dataChannelEnabled: false,
        callback: (info) => {
          if (this.closed) return;
          if (info === "initialized") {
            try {
              this.videoAdaptor.joinRoom(this.roomId, publishId);
            } catch { }
          }
        },
        callbackError: (err) => {
          this.onError(err);
        },
      });

      return true;
    } catch (e) {
      this.onError(e);
      throw e;
    }
  }

  stopPreview(localVideoElement = null) {
    try {
      if (localVideoElement) {
        try {
          localVideoElement.pause();
        } catch { }
        localVideoElement.srcObject = null;
      }
      if (this.localPreviewStream) {
        this.localPreviewStream.getTracks().forEach((t) => t.stop());
        this.localPreviewStream = null;
      }
      if (this.videoAdaptor) {
        try {
          this.videoAdaptor.leaveFromRoom(this.roomId);
        } catch { }
        try {
          this.videoAdaptor.webSocketAdaptor?.close();
        } catch { }
        this.videoAdaptor = null;
      }

      Object.keys(this.playAdaptors).forEach((sid) => this._stopPlayStream(sid));

      return true;
    } catch (e) {
      this.onError(e);
      return false;
    }
  }

  stopLocalTracks() {
    try {
      const stopTracks = (stream) => {
        if (stream) {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch { }
          });
        }
      };

      stopTracks(this.localPreviewStream);
      if (this.webRTCAdaptor?.localStream) {
        stopTracks(this.webRTCAdaptor.localStream);
        this.webRTCAdaptor.localStream = null;
      }
      if (this.videoAdaptor?.localStream) {
        stopTracks(this.videoAdaptor.localStream);
        this.videoAdaptor.localStream = null;
      }
    } catch { }
  }

  async close() {
    if (this.closed) {
      console.log("📡 [V3MediaPTT] Close called but already closed", {
        streamId: this.streamId,
        roomId: this.roomId,
      });
      return;
    }
    console.log("📡 [V3MediaPTT] Closing instance", {
      streamId: this.streamId,
      roomId: this.roomId,
    });
    this.closed = true;
    this._clearReconnect();

    this.stopLocalTracks();

    this._cleanupAdaptor(this.webRTCAdaptor, this.streamId, this.roomId);
    this._cleanupAdaptor(this.videoAdaptor, this.streamId, this.roomId);

    Object.keys(this.playAdaptors).forEach((id) => {
      this._cleanupAdaptor(this.playAdaptors[id], id, this.roomId);
    });

    this.playAdaptors = {};
    this.webRTCAdaptor = null;
    this.videoAdaptor = null;

    try {
      this.audioElement.pause();
      this.audioElement.srcObject = null;
      this.audioElement.load();
    } catch { }

    this._clearSingleUserTimer();
    this.participants.clear();

    this._setState("closed");
  }
}
